import React, { useEffect, useMemo, useState } from 'react';
import {
  Business as BusinessIcon,
  DeleteOutline,
  SettingsSuggest as ConfigurationsIcon,
  UploadFileOutlined
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import BrandedSelect from '../Form/BrandedSelect';
import Loading from '../Loading';
import {
  INDUSTRY_OPTIONS,
  normalizeIndustry
} from '../../constants/industryBenchmarks';
import { ROLES, resolveUserRole } from '../../constants/rolePermissions';
import { sendRequest } from '../../utils/dateHelpers';
import {
  getCertificationLogoFromSource,
  resolveCompanyConfigurations
} from '../../utils/companyConfigurations';

const API_COMPANY_CONFIGURATIONS_URL = '/company-configurations';
const MAX_LOGO_FILE_SIZE = 1024 * 1024 * 1.5;
const ALLOWED_LOGO_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/svg+xml'
];

const getAverageCost = response =>
  resolveCompanyConfigurations(response).average_developer_cost_per_hour;

const getIndustry = response =>
  normalizeIndustry(resolveCompanyConfigurations(response).industry);

const getCertificationLogo = response =>
  getCertificationLogoFromSource(resolveCompanyConfigurations(response));

const normalizeCostValue = value => {
  if (value === null || value === undefined || value === '') return '';

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return '';

  return Math.round(numericValue).toString();
};

const Configurations = () => {
  const { userInfo } = useSelector(state => state);
  const user = userInfo?.user || {};
  const userRole = resolveUserRole(user?.role, user?.roleName, user?.userRole);
  const isCompanyAdmin = userRole === ROLES.ACCOUNT_ADMIN;
  const [values, setValues] = useState({
    average_developer_cost_per_hour: ''
  });
  const [industry, setIndustry] = useState(
    normalizeIndustry(user?.industry || user?.companyIndustry || '')
  );
  const [industryOptions, setIndustryOptions] = useState(INDUSTRY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [certificationLogo, setCertificationLogo] = useState(null);
  const [certificationLogoChanged, setCertificationLogoChanged] =
    useState(false);

  const fieldDefinitions = useMemo(
    () => [
      {
        key: 'industry',
        label: 'Company Industry'
      },
      {
        key: 'average_developer_cost_per_hour',
        label: 'Average Fully Loaded Developer Cost per Hour (USD)',
        placeholder: 'e.g. 150'
      }
    ],
    []
  );

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        setLoading(true);
        const configurationsResponse = await sendRequest(
          API_COMPANY_CONFIGURATIONS_URL,
          'GET'
        );
        const averageCost = normalizeCostValue(
          getAverageCost(configurationsResponse)
        );
        const normalizedIndustry = getIndustry(configurationsResponse);
        const apiIndustries =
          configurationsResponse?.data?.availableIndustries ||
          configurationsResponse?.availableIndustries;
        if (Array.isArray(apiIndustries) && apiIndustries.length > 0) {
          setIndustryOptions(
            apiIndustries.map(option =>
              typeof option === 'string'
                ? { value: option, label: option }
                : option
            )
          );
        }

        setValues({
          average_developer_cost_per_hour: averageCost
        });
        setIndustry(normalizedIndustry);
        setCertificationLogo(getCertificationLogo(configurationsResponse));
        setCertificationLogoChanged(false);
      } catch (error) {
        toast.error(error?.message || 'Failed to load configurations.');
      } finally {
        setLoading(false);
      }
    };

    fetchConfigurations();
  }, []);

  const onInputChange = event => {
    const { name, value } = event.target;

    if (value === '' || /^\d+$/.test(value)) {
      setValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleLogoFileChange = event => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, WEBP, GIF, or SVG logo.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_FILE_SIZE) {
      toast.error('Logo must be 1.5 MB or smaller.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';

      if (!dataUrl) {
        toast.error('Unable to read this logo file.');
        return;
      }

      setCertificationLogo({
        data_url: dataUrl,
        file_name: file.name,
        mime_type: file.type
      });
      setCertificationLogoChanged(true);
    };
    reader.onerror = () => {
      toast.error('Unable to read this logo file.');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleRemoveLogo = () => {
    setCertificationLogo(null);
    setCertificationLogoChanged(true);
  };

  const handleSave = async event => {
    event.preventDefault();

    const rawValue = values.average_developer_cost_per_hour;

    if (rawValue === '') {
      toast.error('Average developer cost per hour is required.');
      return;
    }

    const parsedValue = Number(rawValue);

    if (!Number.isInteger(parsedValue) || parsedValue < 0) {
      toast.error('Average developer cost must be a non-negative integer.');
      return;
    }

    if (!isCompanyAdmin) {
      toast.error(
        'Only company administrators can update company configurations.'
      );
      return;
    }

    if (!industry) {
      toast.error('Please choose an industry before saving.');
      return;
    }

    try {
      setSaving(true);

      const configurationsResponse = await sendRequest(
        API_COMPANY_CONFIGURATIONS_URL,
        'PATCH',
        {
          average_developer_cost_per_hour: parsedValue,
          industry,
          ...(certificationLogoChanged
            ? { certification_logo: certificationLogo }
            : {})
        }
      );

      const savedValue = normalizeCostValue(
        getAverageCost(configurationsResponse) ?? parsedValue
      );
      const normalizedIndustry =
        getIndustry(configurationsResponse) || industry;

      setValues({
        average_developer_cost_per_hour: savedValue
      });
      setIndustry(normalizedIndustry);
      setCertificationLogo(getCertificationLogo(configurationsResponse));
      setCertificationLogoChanged(false);

      toast.success(
        configurationsResponse?.message ||
          'Company configurations updated successfully.'
      );
    } catch (error) {
      toast.error(error?.message || 'Configuration update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-270 space-y-6">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200">
              <ConfigurationsIcon />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black dark:text-white">
                Configurations
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Manage company-level calculation inputs and configuration
                defaults.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6 p-5 sm:p-7">
          {loading && <Loading type="card" />}

          <div className="grid grid-cols-1 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Certification Logo
                  </label>
                  <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                    This logo will appear on certification badges when
                    available. If no logo is set, the company name will be shown
                    instead.
                  </p>
                </div>

                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="flex h-28 w-full max-w-[16rem] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-4 dark:border-slate-600 dark:bg-slate-950">
                    {certificationLogo?.data_url ? (
                      <img
                        src={certificationLogo.data_url}
                        alt="Certification logo preview"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-center text-xs leading-5 text-slate-400 dark:text-slate-500">
                        No logo selected
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-opacity-90">
                      <UploadFileOutlined sx={{ fontSize: 18 }} />
                      {certificationLogo ? 'Change Logo' : 'Upload Logo'}
                      <input
                        type="file"
                        accept={ALLOWED_LOGO_TYPES.join(',')}
                        className="hidden"
                        onChange={handleLogoFileChange}
                        disabled={saving || loading || !isCompanyAdmin}
                      />
                    </label>

                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      disabled={
                        saving ||
                        loading ||
                        !isCompanyAdmin ||
                        !certificationLogo
                      }
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
                    >
                      <DeleteOutline sx={{ fontSize: 18 }} />
                      Remove Logo
                    </button>
                  </div>
                </div>

                {!isCompanyAdmin && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Only company admins can upload or remove this logo.
                  </p>
                )}
              </div>
            </div>

            {fieldDefinitions.map(field => (
              <div
                key={field.key}
                className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40"
              >
                <label
                  htmlFor={field.key}
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {field.label}
                </label>

                {field.key === 'industry' ? (
                  <>
                    <BrandedSelect
                      value={industry}
                      onChange={setIndustry}
                      options={industryOptions}
                      placeholder={
                        loading ? 'Loading industry...' : 'Choose an industry'
                      }
                      leadingIcon={BusinessIcon}
                      isDisabled={loading || saving || !isCompanyAdmin}
                      className="w-full"
                      usePortal={false}
                      minHeight={56}
                      maxMenuHeight={240}
                      menuPlacement="bottom"
                    />
                    {!isCompanyAdmin && (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        Only company admins can change this value.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      id={field.key}
                      name={field.key}
                      type="text"
                      inputMode="numeric"
                      value={values[field.key]}
                      onChange={onInputChange}
                      placeholder={field.placeholder}
                      className="h-14 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      disabled={saving || loading}
                    />
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || loading}
              className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loading type="inline" /> : 'Save Configurations'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Configurations;
